import { Link, LuIcon, stopPropagation } from "@overmap-ai/blocks"
import { truncate } from "@overmap-ai/common"
import {
	type Field,
	FieldSchema,
	type FieldValues,
	formatTime,
	passFailFieldStatusMapping,
	type PassFailFieldValue,
	UNLABELLED_FIELD_LABEL,
	UploadField,
	type ValueOfField,
} from "@overmap-ai/forms"
import type { ColumnDef } from "@tanstack/react-table"
import { cx } from "class-variance-authority"

import { interactiveTextCva } from "../../../cva"
import { modelIconMapping } from "../../../mappings/icons"
import { formatCompactDate } from "../../../utils/date"
import { ColumnSortButton, ColumnSortIcon } from "../components"
import { EmptyColumnCellFallback } from "../components/EmptyColumnCellFallback"

export const getColumnDefOfField = <TData,>(
	field: Exclude<Field, UploadField>,
	getValues: (data: TData) => FieldValues,
): ColumnDef<TData> => {
	switch (field.type) {
		case "boolean":
			return {
				id: field.id,
				accessorFn: (data: TData) => getValues(data)[field.id],
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<ValueOfField<typeof field> | undefined>()
					if (value === undefined || field.isValueBlank(value)) return <EmptyColumnCellFallback />
					return <LuIcon icon={modelIconMapping.check} />
				},
			}
		case "checkbox-list":
			return {
				id: field.id,
				accessorFn: (data: TData) => getValues(data)[field.id],
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<ValueOfField<typeof field> | undefined>()
					if (value === undefined || field.isValueBlank(value)) return <EmptyColumnCellFallback />
					return value.join(", ")
				},
			}
		case "pass-fail":
			return {
				id: field.id,
				accessorFn: (data: TData) => {
					const value = getValues(data)[field.id] as ValueOfField<typeof field> | undefined
					return value?.status
				},
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<PassFailFieldValue["status"] | undefined>()
					if (value === undefined || value === null) return <EmptyColumnCellFallback />
					return passFailFieldStatusMapping[value].label
				},
			}
		case "rating":
			return {
				id: field.id,
				accessorFn: (data: TData) => getValues(data)[field.id],
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<ValueOfField<typeof field> | undefined>()
					if (value === undefined || field.isValueBlank(value)) return <EmptyColumnCellFallback />
					return value
				},
			}
		case "date":
			return {
				id: field.id,
				accessorFn: (data: TData) => getValues(data)[field.id],
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<ValueOfField<typeof field> | undefined>()
					if (value === undefined || field.isValueBlank(value)) return <EmptyColumnCellFallback />
					return formatCompactDate(value!)
				},
			}
		case "multi-select":
			return {
				id: field.id,
				accessorFn: (data: TData) => getValues(data)[field.id],
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<ValueOfField<typeof field> | undefined>()
					if (value === undefined || field.isValueBlank(value)) return <EmptyColumnCellFallback />
					return value.join(", ")
				},
			}
		case "multi-string":
			return {
				id: field.id,
				accessorFn: (data: TData) => getValues(data)[field.id],
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<ValueOfField<typeof field> | undefined>()
					if (value === undefined || field.isValueBlank(value)) return <EmptyColumnCellFallback />
					return value.join(", ")
				},
			}
		case "number":
			return {
				id: field.id,
				accessorFn: (data: TData) => getValues(data)[field.id],
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<ValueOfField<typeof field> | undefined>()
					if (value === undefined || field.isValueBlank(value)) return <EmptyColumnCellFallback />
					return value
				},
			}
		case "radio":
			return {
				id: field.id,
				accessorFn: (data: TData) => getValues(data)[field.id],
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<ValueOfField<typeof field> | undefined>()
					if (value === undefined || field.isValueBlank(value)) return <EmptyColumnCellFallback />
					return value
				},
			}
		case "qr":
			return {
				id: field.id,
				accessorFn: (data: TData) => getValues(data)[field.id],
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<ValueOfField<typeof field> | undefined>()
					if (value === undefined || field.isValueBlank(value)) return <EmptyColumnCellFallback />
					return value
				},
			}
		case "select":
			return {
				id: field.id,
				accessorFn: (data: TData) => getValues(data)[field.id],
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<ValueOfField<typeof field> | undefined>()
					if (value === undefined || field.isValueBlank(value)) return <EmptyColumnCellFallback />
					return value
				},
			}
		case "string":
			return {
				id: field.id,
				accessorFn: (data: TData) => getValues(data)[field.id],
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<ValueOfField<typeof field> | undefined>()
					if (value === undefined || field.isValueBlank(value)) return <EmptyColumnCellFallback />
					return value
				},
			}
		case "text":
			return {
				id: field.id,
				accessorFn: (data: TData) => getValues(data)[field.id],
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<ValueOfField<typeof field> | undefined>()
					if (value === undefined || field.isValueBlank(value)) return <EmptyColumnCellFallback />
					return value
				},
			}
		case "otp":
			return {
				id: field.id,
				accessorFn: (data: TData) => getValues(data)[field.id],
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<ValueOfField<typeof field> | undefined>()
					if (value === undefined || field.isValueBlank(value)) return <EmptyColumnCellFallback />
					return value
				},
			}
		case "url":
			return {
				id: field.id,
				accessorFn: (data: TData) => getValues(data)[field.id],
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<ValueOfField<typeof field> | undefined>()
					if (value === undefined || field.isValueBlank(value)) return <EmptyColumnCellFallback />
					return (
						<Link
							onClick={stopPropagation}
							className={cx("truncate", interactiveTextCva())}
							href={value!.href}
							accentColor="base"
							target="_blank"
							rel="noopener noreferrer"
						>
							{truncate(value!.href, 30)}
						</Link>
					)
				},
			}
		case "time":
			return {
				id: field.id,
				accessorFn: (data: TData) => getValues(data)[field.id],
				sortUndefined: "last",
				enableHiding: true,
				meta: {
					plainHeader: field.label || UNLABELLED_FIELD_LABEL,
				},
				header({ column }) {
					return (
						<ColumnSortButton column={column} variant="ghost" size="sm" accentColor="base">
							{column.columnDef.meta?.plainHeader}
							<ColumnSortIcon sortDirection={column.getIsSorted()} />
						</ColumnSortButton>
					)
				},
				cell({ getValue }) {
					const value = getValue<ValueOfField<typeof field> | undefined>()
					if (value === undefined || field.isValueBlank(value)) return <EmptyColumnCellFallback />
					return formatTime(value!)
				},
			}
	}
}
export const getColumnDefsOfFields = <TData,>(
	fields: Field[],
	getValues: (data: TData) => FieldValues,
): ColumnDef<TData>[] => {
	const supportedFields: Exclude<Field, UploadField>[] = fields.filter((field) => field.type !== "upload")

	return supportedFields.map((field) => {
		return getColumnDefOfField(field, getValues)
	})
}

export const getColumnDefsOfFieldSchema = <TData,>(
	fieldSchema: FieldSchema,
	getValues: (data: TData) => FieldValues,
): ColumnDef<TData>[] => {
	return getColumnDefsOfFields(fieldSchema.getFields(), getValues)
}
